import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '../Icon';
import styles from './Modal.module.scss';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

type ModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title?: ReactNode;
  readonly subtitle?: ReactNode;
  readonly size?: ModalSize;
  readonly closeOnBackdrop?: boolean;
  readonly closeAriaLabel?: string;
  readonly headerActions?: ReactNode;
  readonly footer?: ReactNode;
  readonly children: ReactNode;
  readonly role?: 'dialog' | 'alertdialog';
  readonly compactMobile?: boolean;
};

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  closeOnBackdrop = true,
  closeAriaLabel = 'Close',
  headerActions,
  footer,
  children,
  role = 'dialog',
  compactMobile = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const subtitleId = useId();
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    window.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const animationFrame = window.requestAnimationFrame(() => {
      const autofocusTarget = dialogRef.current?.querySelector<HTMLElement>('[data-autofocus]');
      (autofocusTarget ?? dialogRef.current)?.focus();
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      role="presentation"
      onClick={closeOnBackdrop ? onClose : undefined}
    >
      <div
        ref={dialogRef}
        className={[
          styles.card,
          styles[size],
          compactMobile ? styles.compactMobile : '',
        ].filter(Boolean).join(' ')}
        role={role}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        {(title || headerActions) && (
          <div className={styles.header}>
            <div className={styles.headerText}>
              {title ? <h3 id={titleId} className={styles.title}>{title}</h3> : null}
              {subtitle ? <p id={subtitleId} className={styles.subtitle}>{subtitle}</p> : null}
            </div>
            {headerActions}
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label={closeAriaLabel}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        )}
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
